import { isPartner } from "../../../helpers/commons.helpers";

/** Display labels for client `type` values as shown to an agent. */
const AGENT_TYPE_LABELS = {
  customer: "Customer",
  travel_agency: "Travel Agency",
  travel_access: "Agent",
  business: "Business",
  guest: "Guest",
  ota: "OTA"
};

/**
 * Display labels for client `type` values as shown to a customer.
 * `ota` reads as "Reseller" here (vs. "OTA" for agents) — same underlying
 * type, different label depending on which side is viewing it.
 */
const CUSTOMER_TYPE_LABELS = {
  customer: "Customer",
  travel_agency: "Travel Agency",
  travel_access: "Agent",
  business: "Business",
  guest: "Guest",
  ota: "Reseller",
  reseller: "Reseller"
};

/**
 * Formats a client type code into its agent-facing role label
 *
 * @function formatAgentType
 * @param {string} type - The client `type` field to format
 * @returns {string} The role label for the agent-facing view, "Client" if unknown
 */
export function formatAgentType(type) {
  return AGENT_TYPE_LABELS[type] || "Client";
}

/**
 * Formats a client type code into its customer-facing role label
 *
 * @function formatCustomerType
 * @param {string} type - The client `type` field to format
 * @returns {string} The role label for the customer-facing view, "Client" if unknown
 */
export function formatCustomerType(type) {
  return CUSTOMER_TYPE_LABELS[type] || "Client";
}

/**
 * Builds a client's display name from their first and last name
 *
 * @function getClientFullName
 * @param {Object|null} client - A client record with `first_name`/`last_name`
 * @returns {string} "{first} {last}", or "Unassigned" when there's no client
 */
export function getClientFullName(client) {
  if (!client) return "Unassigned";
  return `${client.first_name} ${client.last_name}`;
}

/**
 * Resolves the back-office route to a customer's record based on their type
 *
 * @function getCustomerRedirectUrl
 * @param {string} clientId - The customer's id
 * @param {string} customerType - The customer's `type` field
 * @returns {string} Route to the customer's record: partners, resellers, or customers
 */
export function getCustomerRedirectUrl(clientId, customerType) {
  if (isPartner(customerType)) {
    return `/partners/${clientId}`;
  } else if (["ota", "reseller"].includes(customerType)) {
    return `/resellers/${clientId}`;
  }
  return `/customers/${clientId}`;
}
